import { Input, Button, Icon, useTranslation } from '@testsigma/atoms';
import { OnboardingAplicationSelector } from './components/application-selection';
import { ApplicationType } from '@testsigma/organism';
import { ApplicationVersion } from '@models/application-version.model';
import { Application } from '@models/application.model';
import { Project } from '@models/project.model';
import ProjectService from '@services/project.service';
import { useState, useCallback, useEffect } from 'react';
import { StrollrEventType } from '@enums/strollr-event-type.enum';
import AmplitudeEventService from '@services/amplitude-event.service';
import StrollEventService from '@services/strollr-event.service';
import VersionService from '@services/version.service';
import { handleApiErrorMessages } from '@utils/api-handler-utils';

interface NewSignUpOnboardingProps {
  switchToProject: (project: Project, isCreateNew: boolean) => void;
}

export const NewSignUpOnboarding: React.FC<NewSignUpOnboardingProps> = ({
  switchToProject,
}) => {
  const { t } = useTranslation();

  const [projectName, setProjectName] = useState('');
  const [applicationType, setApplicationType] = useState<ApplicationType>(
    ApplicationType.WebApplication,
  );

  const [demoProjects, setDemoProjects] = useState<Project[]>([]);
  const [demoProjectVersions, setDemoProjectVersions] = useState<
    ApplicationVersion[]
  >([]);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // function to validate project name
  const validateProjectName = (name: string) => {
    if (!name) {
      setErrorMsg(t('field.required', { field: 'Project Name' }));
      return false;
    } else if (name.length > 250) {
      setErrorMsg(
        t('field.max.chars.error', { field: 'Project Name', size: 250 }),
      );
      return false;
    } else if (name.length < 4) {
      setErrorMsg(
        t('field.min.chars.error', { field: 'Project Name', size: 4 }),
      );
      return false;
    } else {
      setErrorMsg('');
      return true;
    }
  };

  // handle project name change
  const handleProjectNameChange = (value: string) => {
    setProjectName(value);
    validateProjectName(value);
  };

  // onClick continue button to create new project
  const onClickCreateNewProject = useCallback(() => {
    const isValid = validateProjectName(projectName);
    if (!isValid) {
      return;
    }
    setIsCreating(true);
    const project = new Project();
    project.name = projectName;
    project.hasMultipleApps = false;
    project.hasMultipleVersions = false;
    project.description = '';

    const application = new Application();
    application.name = applicationType;
    application.applicationType = applicationType;
    const applicationVersion = new ApplicationVersion();
    applicationVersion.versionName = '1.0';
    application.applicationVersions = [applicationVersion];

    project.applications = [application];

    ProjectService.create(project)
      .then((res: Project) => {
        switchToProject(res, false);
        setDemoProjectUrlToWindow(applicationType);
        sendStrollrTrackForCreateNewProject();
        sendAmplitudeEventOnCreateOrTrySampleProject(true);
        sendAmplitudeEventOnNewSignup();
      })
      .catch((err) => {
        const errorMessage = t('common.deleted.failure', {
          name: t('common.project'),
        });
        handleApiErrorMessages(err, t, errorMessage, t('project.label'));
      })
      .finally(() => {
        setIsCreating(false);
      });
  }, [projectName, applicationType, demoProjects, demoProjectVersions]);

  const setDemoProjectUrlToWindow = (applicationType: ApplicationType) => {
    if (!window.tsGlobal) {
      window.tsGlobal = {};
    }
    const demoProjectVersionId =
      getDemoProjectVersionByApplicationType(applicationType);

    window.tsGlobal.demoProjectUrl =
      window.location.protocol +
      '//' +
      window.location.hostname +
      '/ui/td/' +
      demoProjectVersionId +
      '/cases';
  };

  // when user selects a sample project
  const onClickTrySampleProject = useCallback(() => {
    const sampleProject = getDemoProjectByApplicationType(applicationType);
    switchToProject(sampleProject, false);
    sendStrollrTrackEventForSampleProject();
    sendAmplitudeEventOnCreateOrTrySampleProject(false);
    sendAmplitudeEventOnNewSignup();
  }, [applicationType, demoProjects]);

  // send track event to strollr application on create new project
  const sendStrollrTrackForCreateNewProject = () => {
    StrollEventService.track(StrollrEventType.COMMON_CREATE_PROJECT_CLICK);
  };

  // send track event to strollr application fro sample project selection
  const sendStrollrTrackEventForSampleProject = () => {
    StrollEventService.track(StrollrEventType.COMMON_SAMPLE_PROJECT_CLICK);
  };

  // send track event to Amplitude
  const sendAmplitudeEventOnCreateOrTrySampleProject = (
    isContinue: boolean,
  ) => {
    AmplitudeEventService.onClickContinueOrTrySampleProject(
      true,
      isContinue,
      projectName,
      applicationType,
    );
  };

  // send track event to Amplitude
  const sendAmplitudeEventOnNewSignup = () => {
    AmplitudeEventService.onNewSignup();
  };

  // get demo project id for application type
  const getDemoProjectByApplicationType = (appType: ApplicationType) => {
    let chosenProject = demoProjects[0];
    demoProjects.forEach((project) => {
      if (
        appType === ApplicationType.WebApplication &&
        project.name == 'Simply Travel (Demo)'
      ) {
        chosenProject = project;
      } else if (
        appType === ApplicationType.MobileWeb &&
        project.name == 'Simply Travel Mobile Web (Demo)'
      ) {
        chosenProject = project;
      } else if (
        appType === ApplicationType.IOSNative &&
        project.name == 'Wordpress iOS App (Demo)'
      ) {
        chosenProject = project;
      } else if (
        appType === ApplicationType.AndroidNative &&
        project.name == 'Simply Travel Android App (Demo)'
      ) {
        chosenProject = project;
      }
    });
    return chosenProject;
  };

  // get demo project id for application type
  const getDemoProjectVersionByApplicationType = (appType: ApplicationType) => {
    for (const item of demoProjectVersions) {
      if (
        item.application &&
        item.application.applicationType === applicationType
      ) {
        return item.id;
      }
    }
    return null;
  };

  // fetch demo projects
  const fetchDemoProjects = async () => {
    const searchTerms = 'isDemo:true';
    const demoProjects: Project[] = (await ProjectService.findAll(searchTerms))
      ?.content;
    setDemoProjects(demoProjects);
  };

  // fetch demo projects
  const fetchDemoProjectVersions = async () => {
    const searchTerms = 'isDemo:true';
    const demoProjectVersions: ApplicationVersion[] = (
      await VersionService.findAll(searchTerms)
    )?.content;
    setDemoProjectVersions(demoProjectVersions);
  };

  useEffect(() => {
    fetchDemoProjects();
    fetchDemoProjectVersions();
  }, []);

  return (
    <div className='px-20 py-10 w-[56%] flex-col items-center justify-center'>
      {/* Let's create your first project */}
      <div className=' text-xl font-medium text-unique-600'>
        {t('common.first.project')}
      </div>

      {/* Enter a name and choose application type for your first project  */}
      <div className=' text-sm font-medium text-secondary-500 mt-1'>
        {t('common.first.project.choose')}
      </div>

      {/* Project name */}
      <Input
        id='req-name'
        label={t('common.project.name')}
        value={projectName}
        onChange={handleProjectNameChange}
        type='text'
        wrapperClassName='w-full mt-6'
        inputClassName='ml-1'
        isRequired={true}
        hasError={!!errorMsg}
        errorText={errorMsg}
      />

      {/* Application selector */}
      <div className='mt-8'>
        <OnboardingAplicationSelector
          selectedType={applicationType}
          onSelect={setApplicationType}
        />
      </div>

      {/* Application selector */}
      <div className='w-full flex justify-between mt-16'>
        <Button
          type='primary'
          onClick={onClickCreateNewProject}
          isDisabled={isCreating || !projectName}>
          <div className='flex justify-center items-center gap-x-2'>
            {isCreating && (
              <div className='flex'>
                <Icon name='loader' className='text-base animate-spin' />
              </div>
            )}
            <div>{t('common.continue')}</div>
          </div>
        </Button>

        <div
          className='flex text-unique-500 cursor-pointer gap-x-2'
          onClick={onClickTrySampleProject}>
          <div>
            <Icon name='auto-fix' className='text-xl' />
          </div>
          <div className='text-sm font-medium leading-5'>
            {t('common.try.sample.project')}
          </div>
        </div>
      </div>
    </div>
  );
};
